import {
  View,
  Text,
  StyleSheet,
  TextInput,
  Modal,
  TouchableOpacity,
} from "react-native";
import React, { useState, useEffect } from "react";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import Fonts from "../../../Resources/Fonts";
import CustomAlert from "../../Components/CustomAlert";
import { observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "../../_layout";
import { useTranslation } from "react-i18next";
import ProfileSegmentedControl from "../../Components/ProfileSegmentedControl";
import { formatNumber2Dec } from "../../utils/numberUtils";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import TextareaAutosize from "react-textarea-autosize";
import {
  hasClientProfileUnsavedChanges,
  saveClientProfileDraft,
  syncClientProfileDraftWithPersistedData,
} from "../../platform/guards/clientProfileUnsavedChanges";
import { isPendingInvitationClient } from "../../invitations/invitedClientUiGuards";
import {
  COACH_DASHBOARD_DELETE_FLOW,
  matchesDeleteConfirmationInput,
  resolveDashboardClientActionPolicy,
} from "../../invitations/coachDashboardClientActions";
import { shouldShowNutritionFromSidebarConfig } from "../../nutrition/nutritionSidebarVisibility";
import { CANONICAL_ALLERGEN_CODES } from "../../nutrition/foods/foodClassification";
import { getLocalizedAllergenLabel } from "../../nutrition/shared/localizedAllergens";

const ClientProfile = observer(
  ({ client, profileDiscardResetVersion = 0 }) => {
  const { screenSize } = useScreenDimensions();
  const { t } = useTranslation();
  const { isMobile } = useDevice();

  const [deleteAlertConfig, setDeleteAlertConfig] = useState(null);
  const [typedDeleteConfig, setTypedDeleteConfig] = useState(null);
  const [typedDeleteValue, setTypedDeleteValue] = useState("");
  const [hoveredNutritionAllergenCode, setHoveredNutritionAllergenCode] =
    useState(null);
  const [hoveredNutritionPreferenceKey, setHoveredNutritionPreferenceKey] =
    useState(null);
  const [hoveredNutritionTotalsKey, setHoveredNutritionTotalsKey] =
    useState(null);
  const isInvitationProfile = isPendingInvitationClient(client);
  const shouldShowNutritionSettingsSection = shouldShowNutritionFromSidebarConfig({
    coach: client?.coach,
    sidebarConfig: client?.coach?.sidebarConfig,
  });
  const deletePolicy = resolveDashboardClientActionPolicy({
    client,
  });

  const deleteClient = async () => {
    await client.deleteClient();
  };
  const deleteInvitation = async () => {
    await client.deleteInvitation();
  };

  const closeDeleteDialogs = () => {
    setDeleteAlertConfig(null);
    setTypedDeleteConfig(null);
    setTypedDeleteValue("");
  };

  const openDeleteDialog = () => {
    if (!client || client.isOffline) return;

    if (deletePolicy.deleteFlow === COACH_DASHBOARD_DELETE_FLOW.INVITATION_ONE_CLICK) {
      setDeleteAlertConfig({
        titleKey: "deleteInvitationAlertTitle-string",
        textKey: "deleteInvitationAlertText-string",
        action: deleteInvitation,
      });
      return;
    }

    const isInvitationDelete = deletePolicy.deleteEntity === "invitation";
    setTypedDeleteConfig({
      titleKey: isInvitationDelete
        ? "deleteInvitationAlertTitle-string"
        : "deleteClientAlertTitle-string",
      textKey: isInvitationDelete
        ? "deleteInvitationAlertText-string"
        : "deleteClientAlertText-string",
      expectedValue: deletePolicy.expectedTypedValue,
      placeholderKey: isInvitationDelete
        ? "coachDashboardDeleteInvitationEmailPlaceholder-string"
        : "coachDashboardDeleteAthleteNamePlaceholder-string",
      action: isInvitationDelete ? deleteInvitation : deleteClient,
    });
    setTypedDeleteValue("");
  };

  const isTypedDeleteConfirmEnabled = matchesDeleteConfirmationInput({
    input: typedDeleteValue,
    expectedValue: typedDeleteConfig?.expectedValue,
  });

  const confirmTypedDelete = async () => {
    if (!typedDeleteConfig || !isTypedDeleteConfirmEnabled) return;
    await typedDeleteConfig.action?.();
    closeDeleteDialogs();
  };

  useEffect(() => {
    syncClientProfileDraftWithPersistedData(client);
  }, []);

  const TextName = ({ property, firestoreProperty, placeholder }) => {
    const [keyword, setKeyword] = useState(
      client.getProperty(property) ? client.getProperty(property) : ""
    );
    const onChangeText = (text) => {
      setKeyword(text);
      client.setSettings(firestoreProperty, text);
    };

    const [isHovered, setIsHovered] = useState(false);

    return (
      <View
        style={[
          styles.textInputContainer,
          {
            width: screenSize === "small" ? "80%" : "45%",
            backgroundColor: Colors.whiteColor,
            borderColor: Colors.borderColor,
            borderWidth: 1,
          },
        ]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TextInput
          value={keyword}
          onChangeText={onChangeText}
          style={[
            styles.textInputStyle,
            Fonts.regular14,
            { color: Colors.blackColor, outline: "none", width: "100%" },
          ]}
          placeholder={t(placeholder)}
          placeholderTextColor={Colors.grayColor}
          maxLength={40}
        />
      </View>
    );
  };

  const UnmutableTextName = ({ property, placeholder, textAlign }) => {
    return (
      <View
        style={[
          styles.textInputContainer,
          {
            backgroundColor: "transparent",
            width: screenSize === "small" ? "80%" : "45%",
            paddingHorizontal: 0,
            borderWidth: 0,
          },
        ]}
      >
        <Text
          style={[
            styles.textInputStyle,
            Fonts.regular14,
            { color: Colors.grayColor, width: "100%", lineHeight: 40, textAlign: textAlign || "left" },
          ]}
          numberOfLines={1}
        >
          {client.getProperty(property)
            ? client.getProperty(property)
            : placeholder}
        </Text>
      </View>
    );
  };

  const TextNotes = () => {
    const [string, setString] = useState(client.notes ? client.notes : "");

    const handleChange = (e) => {
      const text = e.target.value;
      setString(text);
      client.setSettings("notes", text);
    };

    return (
      <View style={{ width: "100%", overflow: "hidden" }}>
        <TextareaAutosize
          value={string}
          onChange={handleChange}
          placeholder={t("privateNotePlaceholder-string")}
          style={{
            width: "100%",
            backgroundColor: "transparent",
            border: "none",
            outline: "none",
            color: Colors.blackColor,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            fontSize: 14,
            fontWeight: "400",
            resize: "none",
            padding: 0,
            margin: 0,
            lineHeight: "1.4",
            textAlign: "left",
          }}
          spellCheck={false}
          maxLength={1000}
        />
      </View>
    );
  };

  const propertyMap = {
    squat: { value: client.profileSquat, settingsKey: "profile_squat" },
    benchPress: {
      value: client.profileBenchPress,
      settingsKey: "profile_bench_press",
    },
    deadlift: {
      value: client.profileDeadlift,
      settingsKey: "profile_deadlift",
    },
  };

  const CompetitionRegisterTextBox = ({ propertyCase }) => {
    const { value, settingsKey } = propertyMap[propertyCase] || {};

    const [propertyString, setPropertyString] = useState(
      value > 0 ? formatNumber2Dec(value) : ""
    );
    const [isHovered, setIsHovered] = useState(false);

    const handleChange = (text) => {
      const formattedText = text.replace(",", ".");
      const num = Number(formattedText);
      if (!isNaN(num)) {
        setPropertyString(formattedText);
        const numAux = formattedText === "" ? 0 : num + 1e-7;
        client.setSettings(settingsKey, numAux);
      }
    };

    return (
      <View
        style={[
          styles.competitionTextBoxContainer,
          {
            backgroundColor: Colors.whiteColor,
            borderColor: Colors.borderColor,
            borderWidth: 1,
            width: screenSize === "small" ? "80%" : "45%",
            minWidth: screenSize === "large" ? 100 : undefined,
          },
        ]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TextInput
          value={propertyString}
          onChangeText={handleChange}
          inputMode="numeric"
          style={[styles.competitionTextBoxInput, Fonts.regular14]}
          maxLength={7}
        />
      </View>
    );
  };

  const BestCompetitionLifts = () => {
    const lifts = [
      { key: "squat", label: t("squat-string") },
      { key: "benchPress", label: t("benchPress-string") },
      { key: "deadlift", label: t("deadlift-string") },
    ];

    return (
      <View
        style={[
          styles.bestLiftsContainer,
          { flexDirection: screenSize !== "large" ? "column" : "row" },
        ]}
      >
        {lifts.map((lift, index) => (
          <View
            key={lift.key}
            style={[
              styles.liftRow,
              {
                marginBottom:
                  screenSize !== "large" && index < lifts.length - 1 ? 20 : 0,
                width: screenSize !== "large" ? "100%" : undefined,
              },
            ]}
          >
            <Text
              style={[
                Fonts.regular14,
                styles.liftLabel,
                {
                  width:
                    screenSize === "small"
                      ? 120
                      : screenSize === "medium"
                      ? 180
                      : undefined,
                },
              ]}
            >
              {lift.label}
            </Text>
            <View style={styles.liftTextBoxWrapper}>
              <CompetitionRegisterTextBox propertyCase={lift.key} />
            </View>
          </View>
        ))}
      </View>
    );
  };

  const SaveButton = observer(() => {
    const saveClientProfile = () => {
      saveClientProfileDraft(client);
    };

    const hasChanges = hasClientProfileUnsavedChanges(client);

    const isDisabled = client.isOffline || !hasChanges;

    return (
      <HoverableTouchableOpacity
        onPress={saveClientProfile}
        disabled={isDisabled}
        style={{
          height: 40,
          minWidth: 100,
          borderRadius: 14,
          backgroundColor: Colors.bubbleColor,
          paddingHorizontal: 16,
          alignItems: "center",
          justifyContent: "center",
          opacity: isDisabled ? 0.5 : 1,
        }}
        hoverStyle={{
          backgroundColor: Colors.darkBubbleColor,
        }}
      >
        <Text style={[Fonts.medium14, { color: Colors.whiteColor }]}>
          {t("save-string")}
        </Text>
      </HoverableTouchableOpacity>
    );
  });

  const DeleteButton = () => {
    const deleteButtonText = isInvitationProfile
      ? "deleteInvitation-string"
      : "deleteClient-string";

    return (
      <HoverableTouchableOpacity
        onPress={openDeleteDialog}
        disabled={client.isOffline}
        style={{
          height: 40,
          paddingHorizontal: 16,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 14,
          borderWidth: 1,
          borderColor: "transparent",
          opacity: client.isOffline ? 0.5 : 1,
        }}
        hoverStyle={{
          backgroundColor: "rgba(240, 68, 56, 0.1)", // Light red background
        }}
      >
        <Text style={[Fonts.medium14, { color: Colors.redColor }]}>
          {t(deleteButtonText)}
        </Text>
      </HoverableTouchableOpacity>
    );
  };

  const PendingStatusPill = () => {
    const color = "#d97706";
    const backgroundColor = "rgba(234, 179, 8, 0.1)";

    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          backgroundColor,
          paddingHorizontal: 10,
          paddingVertical: 5,
          borderRadius: 10,
          alignSelf: "flex-start",
          marginBottom: 14,
        }}
      >
        <View
          style={{
            width: 6,
            height: 6,
            borderRadius: 3,
            backgroundColor: color,
            marginRight: 6,
          }}
        />
        <Text
          style={[
            Fonts.regular12,
            {
              color,
              fontWeight: "500",
            },
          ]}
        >
          {t("pending-string")}
        </Text>
      </View>
    );
  };

  const Separator = () => {
    return <View style={styles.separator} />;
  };

  const dietaryPreferenceOptions = [
    { key: "none", label: t("none-string", "None") },
    { key: "vegetarian", label: t("vegetarian-string", "Vegetarian") },
    { key: "vegan", label: t("vegan-string", "Vegan") },
  ];
  const nutritionTotalsVisibilityOptions = [
    {
      key: null,
      label: t(
        "nutritionPlanTotalsUseCoachDefault-string",
        "Use coach default"
      ),
    },
    {
      key: true,
      label: t("nutritionPlanTotalsShowTotals-string", "Show totals"),
    },
    {
      key: false,
      label: t("nutritionPlanTotalsHideTotals-string", "Hide totals"),
    },
  ];
  const selectedAllergenCodes = Array.isArray(client?.clientSettings?.allergen_codes)
    ? client.clientSettings.allergen_codes
    : [];

  const toggleAllergenCode = (allergenCode) => {
    const hasAllergen = selectedAllergenCodes.includes(allergenCode);
    const nextAllergens = hasAllergen
      ? selectedAllergenCodes.filter((entry) => entry !== allergenCode)
      : [...selectedAllergenCodes, allergenCode];

    client.setSettings("allergen_codes", nextAllergens);
  };
  const selectNoAllergens = () => {
    client.setSettings("allergen_codes", []);
  };

  const DeleteSampleClientButton = () => {
    const isSampleClient = client.email && client.email.includes("efortcoach.com");

    if (!isSampleClient) return null;

    return (
      <View style={{ marginBottom: 16, alignSelf: "flex-end" }}>
        <HoverableTouchableOpacity
          onPress={() => {
            if (!client.isOffline) {
              deleteClient();
            }
          }}
          disabled={client.isOffline}
          style={{
            backgroundColor: Colors.redColor,
            borderRadius: 12,
            paddingVertical: 10,
            paddingHorizontal: 16,
            flexDirection: "row",
            alignItems: "center",
            opacity: client.isOffline ? 0.5 : 1,
          }}
          hoverStyle={{
            opacity: 0.85,
          }}
        >
          <Text style={[Fonts.medium14, { color: Colors.whiteColor, fontSize: 13 }]}>
            {t("deleteSampleAthlete-string")}
          </Text>
        </HoverableTouchableOpacity>
      </View>
    );
  };

  return (
    <View
      style={[
        styles.container,
        { paddingTop: screenSize === "small" ? 16 : 24 },
      ]}
    >
      {client && (
        <>
          {!isInvitationProfile && <DeleteSampleClientButton />}

          {isInvitationProfile ? (
            <View style={styles.card}>
              <PendingStatusPill />
              <Text style={[Fonts.medium16, styles.invitationTitle]}>
                {t("pendingInvitationProfileTitle-string")}
              </Text>
              <Text style={[Fonts.regular14, styles.invitationDescription]}>
                {t("pendingInvitationProfileDescription-string")}
              </Text>
            </View>
          ) : (
            <View
              key={`client-profile-reset-${profileDiscardResetVersion}`}
              style={styles.card}
            >
              <View style={styles.row}>
                <Text style={[Fonts.regular14, styles.rowLabel, { width: screenSize === "small" ? 120 : 180 }]}>
                  {t("clientName-string")}
                </Text>
                <View style={[styles.rowContent, styles.nameRowContent]}>
                  <TextName
                    property={"nameForCoach"}
                    firestoreProperty={"name_for_coach"}
                    placeholder={"name-string"}
                  />
                  <View style={styles.nameSurnameSpacer} />
                  <TextName
                    property={"surnameForCoach"}
                    firestoreProperty={"surname_for_coach"}
                    placeholder={"surname-string"}
                  />
                </View>
              </View>
              <Separator />
              <View style={styles.row}>
                <Text style={[Fonts.regular14, styles.rowLabel, { width: screenSize === "small" ? 120 : 180 }]}>
                  {t("email-string")}
                </Text>
                <View style={[styles.rowContent, styles.emailRowContent]}>
                  <View style={styles.emailContentWrapper}>
                    <UnmutableTextName
                      property={"email"}
                      placeholder={"entrenador@gmail.com"}
                      textAlign="center"
                    />
                  </View>
                </View>
              </View>
              <Separator />
              <View style={styles.row}>
                <Text style={[Fonts.regular14, styles.rowLabel, { width: screenSize === "small" ? 120 : 180 }]}>
                  {t("contactNumber-string")}
                </Text>
                <View style={styles.rowContent}>
                  <TextName
                    property={"phone"}
                    firestoreProperty={"phone"}
                    placeholder={"phone-string"}
                  />
                </View>
              </View>
              <Separator />
              <View style={styles.row}>
                <Text style={[Fonts.regular14, styles.rowLabel, { width: screenSize === "small" ? 120 : 180 }]}>
                  {t("gender-string")}
                </Text>
                <View style={styles.rowContent}>
                  <ProfileSegmentedControl
                    options={["female-string", "male-string"]}
                    value={client.clientSettings.gender}
                    onChange={(bool) => client.setSettings("gender", bool)}
                    width={screenSize === "small" ? "100%" : 260}
                  />
                </View>
              </View>
              <Separator />
              <BestCompetitionLifts />

              <Separator />
              <View style={{ width: "100%" }}>
                <TextNotes />
              </View>
            </View>
          )}

          {!isInvitationProfile && shouldShowNutritionSettingsSection ? (
            <View style={[styles.card, styles.nutritionCard]}>
              <Text style={[Fonts.medium16, styles.sectionCardTitle]}>
                {t("nutrition-string")}
              </Text>

              <View style={styles.nutritionSettingsStack}>
                <View style={styles.nutritionField}>
                  <Text style={[Fonts.regular14, styles.nutritionFieldLabel]}>
                    {t("dietaryPreference-string")}
                  </Text>
                  <View style={styles.preferenceChipRow}>
                    {dietaryPreferenceOptions.map((option) => {
                      const isSelected =
                        (client.clientSettings.dietary_preference ??
                          client.dietaryPreference ??
                          "none") === option.key;
                      const isNoneOption = option.key === "none";
                      const showDivider = option.key === "vegetarian";

                      return (
                        <React.Fragment key={option.key}>
                          {showDivider ? (
                            <View style={styles.preferenceSelectionDivider} />
                          ) : null}
                          <TouchableOpacity
                            activeOpacity={0.85}
                            onPress={() =>
                              client.setSettings("dietary_preference", option.key)
                            }
                            onMouseEnter={
                              !isMobile
                                ? () => setHoveredNutritionPreferenceKey(option.key)
                                : undefined
                            }
                            onMouseLeave={
                              !isMobile
                                ? () => setHoveredNutritionPreferenceKey(null)
                                : undefined
                            }
                            style={styles.dietaryOptionTouchable}
                          >
                            <View
                              style={[
                                styles.preferenceChip,
                                isNoneOption ? styles.preferenceChipNone : null,
                                hoveredNutritionPreferenceKey === option.key &&
                                !isSelected
                                  ? styles.preferenceChipHovered
                                  : null,
                                isNoneOption && isSelected
                                  ? styles.preferenceChipNoneActive
                                  : null,
                                isSelected ? styles.preferenceChipActive : null,
                              ]}
                            >
                              <Text
                                style={[
                                  styles.preferenceChipText,
                                  isNoneOption
                                    ? styles.preferenceChipTextNone
                                    : null,
                                  isNoneOption && isSelected
                                    ? styles.preferenceChipTextNoneActive
                                    : null,
                                  isSelected
                                    ? styles.preferenceChipTextActive
                                    : null,
                                ]}
                              >
                                {option.label}
                              </Text>
                            </View>
                          </TouchableOpacity>
                        </React.Fragment>
                      );
                    })}
                  </View>
                </View>

                <View style={styles.sectionDivider} />

                <View style={styles.nutritionField}>
                  <Text style={[Fonts.regular14, styles.nutritionFieldLabel]}>
                    {t("allergens-string")}
                  </Text>
                  <View style={styles.allergenSelectionGrid}>
                    <TouchableOpacity
                      activeOpacity={0.85}
                      onPress={selectNoAllergens}
                      onMouseEnter={
                        !isMobile
                          ? () => setHoveredNutritionAllergenCode("none")
                          : undefined
                      }
                      onMouseLeave={
                        !isMobile
                          ? () => setHoveredNutritionAllergenCode(null)
                          : undefined
                      }
                      style={styles.allergenSelectionTouchable}
                    >
                      <View
                        style={[
                          styles.allergenSelectionChip,
                          styles.allergenSelectionChipNone,
                          hoveredNutritionAllergenCode === "none" &&
                          selectedAllergenCodes.length > 0
                            ? styles.allergenSelectionChipHovered
                            : null,
                          selectedAllergenCodes.length === 0
                            ? styles.allergenSelectionChipNoneActive
                            : null,
                        ]}
                      >
                        <Text
                          style={[
                            styles.allergenSelectionChipText,
                            styles.allergenSelectionChipTextNone,
                            selectedAllergenCodes.length === 0
                              ? styles.allergenSelectionChipTextNoneActive
                              : null,
                          ]}
                        >
                          {t("none-string", "None")}
                        </Text>
                      </View>
                    </TouchableOpacity>
                    <View style={styles.allergenSelectionDivider} />
                    {CANONICAL_ALLERGEN_CODES.map((allergenCode) => {
                      const isSelected = selectedAllergenCodes.includes(allergenCode);

                      return (
                        <TouchableOpacity
                          key={allergenCode}
                          activeOpacity={0.85}
                          onPress={() => toggleAllergenCode(allergenCode)}
                          onMouseEnter={
                            !isMobile
                              ? () => setHoveredNutritionAllergenCode(allergenCode)
                              : undefined
                          }
                          onMouseLeave={
                            !isMobile
                              ? () => setHoveredNutritionAllergenCode(null)
                              : undefined
                          }
                          style={styles.allergenSelectionTouchable}
                        >
                          <View
                            style={[
                              styles.allergenSelectionChip,
                              hoveredNutritionAllergenCode === allergenCode &&
                              !isSelected
                                ? styles.allergenSelectionChipHovered
                                : null,
                              isSelected
                                ? styles.allergenSelectionChipActive
                                : null,
                            ]}
                          >
                            <Text
                              style={[
                                styles.allergenSelectionChipText,
                                isSelected
                                  ? styles.allergenSelectionChipTextActive
                                  : null,
                              ]}
                            >
                              {getLocalizedAllergenLabel(allergenCode, t)}
                            </Text>
                          </View>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>

                <View style={styles.sectionDivider} />

                <View style={styles.nutritionField}>
                  <Text style={[Fonts.regular14, styles.nutritionFieldLabel]}>
                    {t(
                      "nutritionPlanTotalsVisibility-string",
                      "Show calories and macros"
                    )}
                  </Text>
                  <View style={styles.preferenceChipRow}>
                    {nutritionTotalsVisibilityOptions.map((option) => {
                      const isSelected =
                        (client.clientSettings
                          .show_nutrition_plan_totals_override ?? null) ===
                        option.key;
                      const isDefaultOption = option.key === null;
                      const showDivider = option.key === true;

                      return (
                        <React.Fragment key={String(option.key)}>
                          {showDivider ? (
                            <View style={styles.preferenceSelectionDivider} />
                          ) : null}
                          <TouchableOpacity
                            activeOpacity={0.85}
                            onPress={() =>
                              client.setSettings(
                                "show_nutrition_plan_totals_override",
                                option.key
                              )
                            }
                            onMouseEnter={
                              !isMobile
                                ? () => setHoveredNutritionTotalsKey(option.key)
                                : undefined
                            }
                            onMouseLeave={
                              !isMobile
                                ? () => setHoveredNutritionTotalsKey(null)
                                : undefined
                            }
                            style={styles.dietaryOptionTouchable}
                          >
                            <View
                              style={[
                                styles.preferenceChip,
                                isDefaultOption ? styles.preferenceChipNone : null,
                                hoveredNutritionTotalsKey === option.key &&
                                !isSelected
                                  ? styles.preferenceChipHovered
                                  : null,
                                isDefaultOption && isSelected
                                  ? styles.preferenceChipNoneActive
                                  : null,
                                isSelected ? styles.preferenceChipActive : null,
                              ]}
                            >
                              <Text
                                style={[
                                  styles.preferenceChipText,
                                  isDefaultOption
                                    ? styles.preferenceChipTextNone
                                    : null,
                                  isDefaultOption && isSelected
                                    ? styles.preferenceChipTextNoneActive
                                    : null,
                                  isSelected
                                    ? styles.preferenceChipTextActive
                                    : null,
                                ]}
                              >
                                {option.label}
                              </Text>
                            </View>
                          </TouchableOpacity>
                        </React.Fragment>
                      );
                    })}
                  </View>
                </View>
              </View>
            </View>
          ) : null}

          <View style={styles.actionsContainer}>
            {!isInvitationProfile && <SaveButton />}
            <DeleteButton />
          </View>

          {deleteAlertConfig && (
            <CustomAlert
              isVisible={!!deleteAlertConfig}
              onClose={closeDeleteDialogs}
              title={deleteAlertConfig.titleKey}
              text={deleteAlertConfig.textKey}
              action={deleteAlertConfig.action}
              specialCTA={"delete-string"}
            />
          )}

          {typedDeleteConfig && (
            <Modal
              animationType="fade"
              transparent={true}
              visible={!!typedDeleteConfig}
              onRequestClose={closeDeleteDialogs}
            >
              <View style={GlobalStyles.centeredView}>
                <TouchableOpacity
                  onPress={closeDeleteDialogs}
                  style={GlobalStyles.touchableBackground}
                >
                  <View />
                </TouchableOpacity>
                <View
                  style={{ position: "absolute" }}
                  onStartShouldSetResponder={() => false}
                >
                  <View
                    style={[
                      styles.typedDeleteModalView,
                      {
                        width: screenSize === "small" ? 300 : 400,
                        maxWidth: screenSize === "small" ? 300 : 400,
                      },
                    ]}
                  >
                    <Text style={[Fonts.bold18Centered, { marginBottom: 12 }]}>
                      {t(typedDeleteConfig.titleKey)}
                    </Text>
                    <Text style={styles.typedDeleteDescription}>
                      {t(typedDeleteConfig.textKey)}
                    </Text>
                    <Text style={styles.typedDeleteInstruction}>
                      {t("coachDashboardTypeToConfirmDelete-string", {
                        expectedValue: typedDeleteConfig.expectedValue,
                      })}
                    </Text>

                    <TextInput
                      style={styles.typedDeleteInput}
                      value={typedDeleteValue}
                      onChangeText={setTypedDeleteValue}
                      onSubmitEditing={() => {
                        void confirmTypedDelete();
                      }}
                      placeholder={t(typedDeleteConfig.placeholderKey)}
                      placeholderTextColor={Colors.grayColor}
                      autoCapitalize="none"
                      autoCorrect={false}
                      maxLength={120}
                    />

                    <View style={styles.typedDeleteActions}>
                      <TouchableOpacity onPress={closeDeleteDialogs}>
                        <View style={styles.typedDeleteCancelButton}>
                          <Text style={styles.typedDeleteCancelButtonText}>
                            {t("cancel-string")}
                          </Text>
                        </View>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={confirmTypedDelete}
                        disabled={!isTypedDeleteConfirmEnabled}
                      >
                        <View
                          style={[
                            styles.typedDeleteConfirmButton,
                            !isTypedDeleteConfirmEnabled &&
                              styles.typedDeleteConfirmButtonDisabled,
                          ]}
                        >
                          <Text style={styles.typedDeleteConfirmButtonText}>
                            {t("delete-string")}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              </View>
            </Modal>
          )}
        </>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    backgroundColor: Colors.whiteColor,
    padding: 24,
    borderRadius: 14,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
  },
  nutritionCard: {
    marginTop: 18,
  },
  invitationTitle: {
    color: Colors.blackColor,
    marginBottom: 4,
    fontSize: 15,
    textAlign: "left",
    fontWeight: "500",
    marginTop: 0,
  },
  invitationDescription: {
    color: Colors.grayColor,
    lineHeight: 22,
    fontSize: 14,
    textAlign: "left",
    width: "100%",
    marginTop: 2,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
  },
  rowLabel: {
    color: Colors.blackColor,
  },
  rowContent: {
    flexDirection: "row",
    flex: 1,
    alignItems: "center",
    justifyContent: "space-around",
    flexWrap: "wrap",
  },
  nameRowContent: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  emailRowContent: {
    justifyContent: "center",
  },
  emailContentWrapper: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  nameSurnameSpacer: {
    marginVertical: 6,
  },
  separator: {
    height: 1,
    backgroundColor: Colors.lightGrayColor,
    width: "100%",
    marginVertical: 20,
  },
  textInputStyle: {
    height: 40,
  },
  textInputContainer: {
    borderRadius: 12,
    paddingHorizontal: 12,
    height: 40,
    justifyContent: "center",
  },
  competitionTextBoxContainer: {
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  competitionTextBoxInput: {
    width: "100%",
    textAlign: "center",
    color: Colors.blackColor,
    paddingVertical: 8,
    outlineStyle: "none",
  },
  bestLiftsContainer: {
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  liftRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  liftLabel: {
    color: Colors.blackColor,
  },
  liftTextBoxWrapper: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
  },
  sectionCardTitle: {
    color: Colors.blackColor,
    marginBottom: 6,
  },
  sectionCardDescription: {
    color: Colors.grayColor,
    lineHeight: 20,
  },
  nutritionSettingsStack: {
    marginTop: 20,
    gap: 18,
  },
  nutritionField: {
    width: "100%",
    gap: 10,
  },
  nutritionFieldLabel: {
    color: Colors.blackColor,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: Colors.lightGrayColor,
    width: "100%",
  },
  allergenSelectionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
  },
  allergenSelectionTouchable: {
    borderRadius: 12,
  },
  allergenSelectionDivider: {
    width: 1,
    height: 18,
    borderRadius: 999,
    backgroundColor: Colors.borderColor,
    marginHorizontal: 2,
  },
  allergenSelectionChip: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 10,
    paddingVertical: 6,
    transitionProperty: "border-color, background-color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  allergenSelectionChipHovered: {
    borderColor: Colors.grayColor,
  },
  allergenSelectionChipActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.06)",
  },
  allergenSelectionChipNone: {
    backgroundColor: Colors.lightGrayColor,
  },
  allergenSelectionChipNoneActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.06)",
  },
  allergenSelectionChipText: {
    ...Fonts.medium12,
    fontSize: 11,
    color: Colors.darkgrayColor,
    transitionProperty: "color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  allergenSelectionChipTextActive: {
    color: Colors.bubbleColor,
  },
  allergenSelectionChipTextNone: {
    color: Colors.darkgrayColor,
    fontWeight: "600",
  },
  allergenSelectionChipTextNoneActive: {
    color: Colors.bubbleColor,
  },
  preferenceChipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
  },
  dietaryOptionTouchable: {
    borderRadius: 12,
  },
  preferenceSelectionDivider: {
    width: 1,
    height: 18,
    borderRadius: 999,
    backgroundColor: Colors.borderColor,
    marginHorizontal: 2,
  },
  preferenceChip: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 10,
    paddingVertical: 6,
    transitionProperty: "border-color, background-color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  preferenceChipNone: {
    backgroundColor: Colors.lightGrayColor,
  },
  preferenceChipHovered: {
    borderColor: Colors.grayColor,
  },
  preferenceChipNoneActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.06)",
  },
  preferenceChipActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.06)",
  },
  preferenceChipText: {
    ...Fonts.medium12,
    fontSize: 11,
    color: Colors.darkgrayColor,
    transitionProperty: "color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  preferenceChipTextNone: {
    color: Colors.darkgrayColor,
    fontWeight: "600",
  },
  preferenceChipTextActive: {
    color: Colors.bubbleColor,
  },
  preferenceChipTextNoneActive: {
    color: Colors.bubbleColor,
  },
  actionsContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: 24,
    gap: 16,
  },
  typedDeleteModalView: {
    alignSelf: "center",
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    paddingHorizontal: 24,
    paddingVertical: 22,
  },
  typedDeleteDescription: {
    color: Colors.blackColor,
    textAlign: "center",
    marginBottom: 12,
    lineHeight: 20,
  },
  typedDeleteInstruction: {
    color: Colors.grayColor,
    textAlign: "center",
    marginBottom: 12,
    lineHeight: 18,
    fontSize: 12,
  },
  typedDeleteInput: {
    borderWidth: 1,
    borderColor: Colors.borderColor,
    borderRadius: 12,
    paddingHorizontal: 12,
    height: 40,
    outlineStyle: "none",
    color: Colors.blackColor,
  },
  typedDeleteActions: {
    ...GlobalStyles.HStack,
    justifyContent: "space-around",
    marginTop: 18,
  },
  typedDeleteCancelButton: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.redColor,
    width: 90,
    height: 35,
    alignItems: "center",
    justifyContent: "center",
  },
  typedDeleteCancelButtonText: {
    color: Colors.redColor,
  },
  typedDeleteConfirmButton: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.redColor,
    backgroundColor: Colors.redColor,
    width: 90,
    height: 35,
    alignItems: "center",
    justifyContent: "center",
  },
  typedDeleteConfirmButtonDisabled: {
    opacity: 0.45,
  },
  typedDeleteConfirmButtonText: {
    color: Colors.whiteColor,
  },
});

export default ClientProfile;
